import { planOfflineRecovery, type RecoveryCommand } from "./offline-recovery-model";
import { planFailedPhotoReplacement, type ArchivedOfflineRequest, type PhotoReplacementCommand } from "./offline-photo-replacement";
import { planAbandonedOfflineGroupRestore, planOfflineGroupAbandonment, type AbandonedOfflineRequest, type AbandonOfflineGroupCommand, type RestoreAbandonedOfflineGroupCommand } from "./offline-group-abandonment";
export type OfflineRequestInput = {
  dedupeKey: string;
  operationKey?: string;
  serverBase?: string;
  groupId: string;
  sequence: number;
  path: string;
  method: string;
  body: string;
  label: string;
  backgroundEligible?: boolean;
};

export type OfflineRequest = OfflineRequestInput & {
  id: string;
  scopeKey: string;
  status: "pending" | "failed";
  attempts: number;
  lastError: string;
  createdAt: string;
  updatedAt: string;
  backgroundEligible: boolean;
  recovery?: { paused: boolean; changedAt: string; serverConfirmedAt?: string };
};

type OfflineSnapshot<T = unknown> = {
  id: string;
  scopeKey: string;
  snapshotKey: string;
  data: T;
  updatedAt: string;
};

const databaseName = "wenjing-mobile-offline";
const databaseVersion = 1;
const requestStore = "requests";
const snapshotStore = "snapshots";

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("离线存储操作失败"));
  });
}

function transactionComplete(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error("离线存储事务失败"));
    transaction.onabort = () => reject(transaction.error || new Error("离线存储事务已取消"));
  });
}

async function inTransaction<T>(database: IDBDatabase, name: string, mode: IDBTransactionMode, work: (store: IDBObjectStore) => Promise<T> | T): Promise<T> {
  const transaction = database.transaction(name, mode);
  const completed = transactionComplete(transaction);
  // 请求和事务可能同时失败；立刻观察事务异常，避免未处理的拒绝。
  void completed.catch(() => undefined);
  try {
    const result = await work(transaction.objectStore(name));
    await completed;
    return result;
  } catch (error) {
    // 同步序列化/put异常也必须回滚，不能只留下整组的前半部分。
    try { transaction.abort(); } catch { /* 已结束的事务无法再次取消。 */ }
    await completed.catch(() => undefined);
    throw error;
  }
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (!globalThis.indexedDB) return reject(new Error("当前设备不支持离线存储"));
    const request = globalThis.indexedDB.open(databaseName, databaseVersion);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(requestStore)) {
        const store = database.createObjectStore(requestStore, { keyPath: "id" });
        store.createIndex("scopeKey", "scopeKey", { unique: false });
      }
      if (!database.objectStoreNames.contains(snapshotStore)) {
        const store = database.createObjectStore(snapshotStore, { keyPath: "id" });
        store.createIndex("scopeKey", "scopeKey", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("无法打开离线存储"));
  });
}

function identifier(prefix: string) {
  return globalThis.crypto?.randomUUID ? `${prefix}-${globalThis.crypto.randomUUID()}` : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "补传失败";
}
export function isBackgroundEligible(item: OfflineRequestInput & { recovery?: unknown }) { return !item.recovery && item.backgroundEligible !== false && !item.path.includes("/evidence/upload") && item.body.length <= 262144; }
export function isRetryableRequestError(error: unknown) { const status = Number((error as { status?: number })?.status || 0); return status === 408 || status === 429 || status >= 500; }

export function prepareOfflineRequests(scopeKey: string, existing: OfflineRequest[], inputs: OfflineRequestInput[], createId: () => string = () => identifier("offline"), clock: () => number = () => Date.now()) {
  const known = new Set(existing.filter((item) => item.scopeKey === scopeKey).map((item) => item.dedupeKey));
  const operations = new Set(existing.filter(item => item.scopeKey === scopeKey && item.operationKey).map(item => JSON.stringify([item.serverBase, item.operationKey])));
  const unique = inputs.filter((item) => { if (item.operationKey && operations.has(JSON.stringify([item.serverBase, item.operationKey])) || known.has(item.dedupeKey)) return false; known.add(item.dedupeKey); return true; });
  const baseTime = clock();
  return unique.map<OfflineRequest>((item, index) => ({
    ...item,
    id: createId(),
    scopeKey,
    status: "pending",
    attempts: 0,
    lastError: "",
    createdAt: new Date(baseTime + index).toISOString(),
    updatedAt: new Date(baseTime).toISOString(),
    backgroundEligible: isBackgroundEligible(item)
  }));
}

export function isNetworkError(error: unknown) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const message = errorMessage(error).toLowerCase();
  return error instanceof TypeError || message.includes("failed to fetch") || message.includes("networkerror") || message.includes("network request failed") || message.includes("load failed");
}

export async function listOfflineRequests(scopeKey: string) {
  const database = await openDatabase();
  try {
    const items = await inTransaction(database, requestStore, "readonly", store => requestResult(store.index("scopeKey").getAll(scopeKey))) as OfflineRequest[];
    return items.sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.sequence - right.sequence);
  } finally { database.close(); }
}

export async function enqueueOfflineRequests(scopeKey: string, inputs: OfflineRequestInput[]) {
  const database = await openDatabase();
  try {
    // 读取和去重写入同一个readwrite事务，多页面同时入队也只能保留原请求键。
    return await inTransaction(database, requestStore, "readwrite", async store => {
      const existing = await requestResult(store.index("scopeKey").getAll(scopeKey)) as OfflineRequest[];
      const additions = prepareOfflineRequests(scopeKey, existing, inputs);
      for (const item of additions) store.put(item);
      return [...existing, ...additions].sort((a,b) => a.createdAt.localeCompare(b.createdAt) || a.sequence-b.sequence);
    });
  } finally { database.close(); }
}

async function saveRequest(item: OfflineRequest) {
  const database = await openDatabase();
  try {
    await inTransaction(database, requestStore, "readwrite", store => { store.put(item); });
  } finally { database.close(); }
}

async function removeRequest(id: string) {
  const database = await openDatabase();
  try {
    await inTransaction(database, requestStore, "readwrite", store => { store.delete(id); });
  } finally { database.close(); }
}

export type OfflineQueueStore = {
  list: (scopeKey: string) => Promise<OfflineRequest[]>;
  enqueue: (scopeKey: string, inputs: OfflineRequestInput[]) => Promise<OfflineRequest[]>;
  save: (item: OfflineRequest) => Promise<void>;
  remove: (id: string) => Promise<void>;
};
const indexedQueue: OfflineQueueStore = { list: listOfflineRequests, enqueue: enqueueOfflineRequests, save: saveRequest, remove: removeRequest };
const localLocks = new Map<string, Promise<unknown>>();
export async function withOfflineQueueLock<T>(scopeKey: string, work: () => Promise<T>): Promise<T> {
  if (globalThis.navigator?.locks) return navigator.locks.request("wenjing-outbox:" + scopeKey, work);
  const previous = localLocks.get(scopeKey) || Promise.resolve();
  const next = previous.catch(() => undefined).then(work); localLocks.set(scopeKey, next);
  try { return await next; } finally { if (localLocks.get(scopeKey) === next) localLocks.delete(scopeKey); }
}
export async function flushOfflineRequests(scopeKey: string, sender: (request: OfflineRequest) => Promise<unknown>, options: { retryFailed?: boolean } = {}, storage: OfflineQueueStore = indexedQueue) {
  const items = await storage.list(scopeKey);
  let sent = 0, networkBlocked = false, retryBlocked = false;
  const responses: Array<{ dedupeKey: string; value: unknown }> = [];
  const groups = new Map<string, OfflineRequest[]>();
  for (const item of items.filter(item => item.scopeKey === scopeKey)) { const rows = groups.get(item.groupId) || []; rows.push(item); groups.set(item.groupId, rows); }
  outer: for (const rows of groups.values()) {
    if (rows.some(item => item.recovery?.paused)) continue;
    rows.sort((a,b) => a.sequence-b.sequence || a.createdAt.localeCompare(b.createdAt));
    for (const item of rows) {
      if (item.status === "failed" && !options.retryFailed) break;
      let value: unknown;
      try { value = await sender(item); }
      catch (error) {
        const networkFailure = isNetworkError(error), retryableFailure = isRetryableRequestError(error);
        networkBlocked ||= networkFailure; retryBlocked ||= retryableFailure;
        await storage.save({ ...item, status: networkFailure || retryableFailure ? "pending" : "failed", attempts: item.attempts + 1, lastError: errorMessage(error), updatedAt: new Date().toISOString() });
        if (networkFailure) break outer;
        break; // 所有失败均阻断同组后续动作；其他组仍可继续。
      }
      // 持久删除失败必须停止，不能当作HTTP失败继续发送后续完工。
      await storage.remove(item.id); sent += 1; responses.push({ dedupeKey: item.dedupeKey, value });
    }
  }
  const remaining = await storage.list(scopeKey);
  return { sent, remaining, responses, failed: remaining.filter(item => item.status === "failed").length, networkBlocked, retryBlocked };
}
export async function submitDurableRequests(scopeKey: string, inputs: OfflineRequestInput[], sender: (request: OfflineRequest) => Promise<unknown>, online: boolean, storage: OfflineQueueStore = indexedQueue) {
  const saved = await storage.enqueue(scopeKey, inputs); // 事务提交之前绝不发网络请求。
  const matches = (item: OfflineRequest) => inputs.some(input => input.operationKey ? input.operationKey === item.operationKey && input.serverBase === item.serverBase : input.dedupeKey === item.dedupeKey);
  const originalKeys = new Set(saved.filter(matches).map(item => item.dedupeKey));
  const result = online ? await flushOfflineRequests(scopeKey, sender, {}, storage) : { remaining: saved, responses: [], failed: saved.filter(item => item.status === "failed").length, networkBlocked: true, retryBlocked: false, sent: 0 };
  return { ...result, queued: result.remaining.some(matches), responses: result.responses.filter(item => originalKeys.has(item.dedupeKey)).map(item => item.value) };
}
export function foregroundOnlyGroups(items: OfflineRequest[]) {
  return new Set(items.filter(item => !isBackgroundEligible(item)).map(item => item.groupId));
}

export async function reconcileOfflineRequests(scopeKey: string, knownIds: string[], nativeItems: OfflineRequest[]) {
  const known = new Set(knownIds), remaining = new Map(nativeItems.filter((item) => item.scopeKey === scopeKey).map((item) => [item.id, { ...item, scopeKey }]));
  const database = await openDatabase();
  try {
    await inTransaction(database, requestStore, "readwrite", async store => {
      const local = await requestResult(store.index("scopeKey").getAll(scopeKey)) as OfflineRequest[];
      const foreground = foregroundOnlyGroups(local);
      for (const item of local) if (!foreground.has(item.groupId) && known.has(item.id) && !remaining.has(item.id)) store.delete(item.id);
      for (const item of remaining.values()) if (!foreground.has(item.groupId)) store.put(item);
    });
  } finally { database.close(); }
  return listOfflineRequests(scopeKey);
}

export async function recoverOfflineGroup(scopeKey: string, command: RecoveryCommand) {
  const database = await openDatabase();
  try {
    return await inTransaction(database, requestStore, "readwrite", async store => {
      const local = await requestResult(store.index("scopeKey").getAll(scopeKey)) as OfflineRequest[];
      const changed = planOfflineRecovery(scopeKey, local, command);
      for (const item of changed) store.put(item);
      const replacements = new Map(changed.map(item => [item.id, item]));
      return local.map(item => replacements.get(item.id) || item);
    });
  } finally { database.close(); }
}


/** 原失败记录写入快照归档、旧活动记录移除、新请求写入，三步必须同一 IndexedDB 事务提交。 */
export async function replaceFailedOfflinePhoto(scopeKey: string, command: PhotoReplacementCommand) {
  const database = await openDatabase();
  const transaction = database.transaction([requestStore, snapshotStore], "readwrite");
  const completed = transactionComplete(transaction); void completed.catch(() => undefined);
  try {
    const requests = transaction.objectStore(requestStore), snapshots = transaction.objectStore(snapshotStore);
    const local = await requestResult(requests.index("scopeKey").getAll(scopeKey)) as OfflineRequest[];
    const plan = planFailedPhotoReplacement(scopeKey, local, command, () => identifier("offline-replacement"));
    for (const item of plan.archived) {
      const snapshotKey = `offline-archive:${item.id}`;
      const archive: OfflineSnapshot<ArchivedOfflineRequest> = { id: `${scopeKey}:${snapshotKey}`, scopeKey, snapshotKey, data: item, updatedAt: item.archive.archivedAt };
      snapshots.put(archive); requests.delete(item.id);
    }
    for (const item of plan.replacements) requests.put(item);
    await completed;
    const removed = new Set(plan.archived.map(item => item.id));
    return [...local.filter(item => !removed.has(item.id)), ...plan.replacements].sort((a,b) => a.createdAt.localeCompare(b.createdAt) || a.sequence-b.sequence);
  } catch (error) {
    try { transaction.abort(); } catch { /* 已结束的事务无法再次取消。 */ }
    await completed.catch(() => undefined);
    throw error;
  } finally { database.close(); }
}

function isAbandonedOfflineRequest(value: unknown): value is AbandonedOfflineRequest {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AbandonedOfflineRequest>;
  return candidate.archive?.reason === "group_abandoned" && typeof candidate.archive.abandonmentId === "string";
}

/** 原组先永久归档再移出活动队列；两步必须同一 IndexedDB 事务提交。 */
export async function abandonOfflineGroup(scopeKey: string, command: AbandonOfflineGroupCommand) {
  const database = await openDatabase();
  const transaction = database.transaction([requestStore, snapshotStore], "readwrite");
  const completed = transactionComplete(transaction); void completed.catch(() => undefined);
  try {
    const requests = transaction.objectStore(requestStore), snapshots = transaction.objectStore(snapshotStore);
    const local = await requestResult(requests.index("scopeKey").getAll(scopeKey)) as OfflineRequest[];
    const snapshotRows = await requestResult(snapshots.index("scopeKey").getAll(scopeKey)) as OfflineSnapshot<unknown>[];
    const existingAbandonmentIds = new Set(snapshotRows.filter(row => isAbandonedOfflineRequest(row.data)).map(row => (row.data as AbandonedOfflineRequest).archive.abandonmentId));
    const createAbandonmentId = () => {
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const candidate = identifier("offline-abandonment");
        if (!existingAbandonmentIds.has(candidate)) return candidate;
      }
      throw new Error("无法生成唯一的整组放弃审计标识，请重试");
    };
    const plan = planOfflineGroupAbandonment(scopeKey, local, command, createAbandonmentId);
    for (const item of plan.archived) {
      const snapshotKey = `offline-abandon:${item.archive.abandonmentId}:${item.id}`;
      const archive: OfflineSnapshot<AbandonedOfflineRequest> = { id: `${scopeKey}:${snapshotKey}`, scopeKey, snapshotKey, data: item, updatedAt: item.archive.archivedAt };
      snapshots.put(archive);
      requests.delete(item.id);
    }
    await completed;
    const removed = new Set(plan.archived.map(item => item.id));
    return local.filter(item => !removed.has(item.id));
  } catch (error) {
    try { transaction.abort(); } catch { /* 已结束的事务无法再次取消。 */ }
    await completed.catch(() => undefined);
    throw error;
  } finally { database.close(); }
}

/** 误操作恢复会保留永久归档，并把原请求恢复为暂停、仅前台处理；不会自动发送。 */
export async function restoreAbandonedOfflineGroup(scopeKey: string, command: RestoreAbandonedOfflineGroupCommand) {
  const database = await openDatabase();
  const transaction = database.transaction([requestStore, snapshotStore], "readwrite");
  const completed = transactionComplete(transaction); void completed.catch(() => undefined);
  try {
    const requests = transaction.objectStore(requestStore), snapshots = transaction.objectStore(snapshotStore);
    const local = await requestResult(requests.index("scopeKey").getAll(scopeKey)) as OfflineRequest[];
    const snapshotRows = await requestResult(snapshots.index("scopeKey").getAll(scopeKey)) as OfflineSnapshot<unknown>[];
    const archiveRows = snapshotRows.filter(row => row.snapshotKey.startsWith(`offline-abandon:${command.abandonmentId}:`) && isAbandonedOfflineRequest(row.data));
    const plan = planAbandonedOfflineGroupRestore(scopeKey, local, archiveRows.map(row => row.data as AbandonedOfflineRequest), command);
    const updates = new Map(plan.updatedArchives.map(item => [item.id, item]));
    for (const row of archiveRows) {
      const updated = updates.get((row.data as AbandonedOfflineRequest).id);
      if (updated) snapshots.put({ ...row, data: updated, updatedAt: updated.archive.restoredAt || row.updatedAt });
    }
    for (const item of plan.restored) requests.put(item);
    await completed;
    return [...local, ...plan.restored].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.sequence - b.sequence);
  } catch (error) {
    try { transaction.abort(); } catch { /* 已结束的事务无法再次取消。 */ }
    await completed.catch(() => undefined);
    throw error;
  } finally { database.close(); }
}

export async function listAbandonedOfflineRequests(scopeKey: string) {
  const database = await openDatabase();
  try {
    const snapshots = await inTransaction(database, snapshotStore, "readonly", store => requestResult(store.index("scopeKey").getAll(scopeKey))) as OfflineSnapshot<unknown>[];
    return snapshots
      .filter(item => item.snapshotKey.startsWith("offline-abandon:") && isAbandonedOfflineRequest(item.data))
      .map(item => item.data as AbandonedOfflineRequest)
      .filter(item => !item.archive.restoredAt)
      .sort((a, b) => b.archive.archivedAt.localeCompare(a.archive.archivedAt) || a.sequence - b.sequence);
  } finally { database.close(); }
}
export async function listArchivedOfflineRequests(scopeKey: string) {
  const database = await openDatabase();
  try {
    const snapshots = await inTransaction(database, snapshotStore, "readonly", store => requestResult(store.index("scopeKey").getAll(scopeKey))) as OfflineSnapshot<ArchivedOfflineRequest>[];
    return snapshots.filter(item => item.snapshotKey.startsWith("offline-archive:")).map(item => item.data).sort((a,b) => b.archive.archivedAt.localeCompare(a.archive.archivedAt));
  } finally { database.close(); }
}

export async function saveOfflineSnapshot<T>(scopeKey: string, snapshotKey: string, data: T) {
  const database = await openDatabase();
  try {
    const item: OfflineSnapshot<T> = { id: `${scopeKey}:${snapshotKey}`, scopeKey, snapshotKey, data, updatedAt: new Date().toISOString() };
    await inTransaction(database, snapshotStore, "readwrite", store => { store.put(item); });
  } finally { database.close(); }
}

export async function loadOfflineSnapshot<T>(scopeKey: string, snapshotKey: string) {
  const database = await openDatabase();
  try {
    const item = await inTransaction(database, snapshotStore, "readonly", store => requestResult(store.get(`${scopeKey}:${snapshotKey}`))) as OfflineSnapshot<T> | undefined;
    return item || null;
  } finally { database.close(); }
}
